import { useEffect, useState, useRef, useCallback, useLayoutEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import { api, swipeProfile } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Send, X, Check, Heart, Users, MapPin,
  GraduationCap, Briefcase, Baby, Shield, Phone, Loader2, Info,
} from "lucide-react";

const CHAT_COLORS = {
  hiv: { bg: "bg-red-500", lightBg: "bg-red-50", text: "text-red-700" },
  hsv: { bg: "bg-green-500", lightBg: "bg-green-50", text: "text-green-700" },
  hpv: { bg: "bg-blue-500", lightBg: "bg-blue-50", text: "text-blue-700" },
};

const PAGE_SIZE = 15;                 // matches Flutter
const POLL_INTERVAL_MS = 5000;        // matches Flutter (5s)
const SCROLL_TOP_THRESHOLD = 120;     // px from top to trigger older-message fetch
const NEAR_BOTTOM_THRESHOLD = 150;    // px from bottom to consider user "at bottom"

const formatDateHeader = (date) => {
  const now = new Date();
  const messageDate = new Date(date);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const msgDay = new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate());

  if (msgDay.getTime() === today.getTime()) return "Today";
  if (msgDay.getTime() === yesterday.getTime()) return "Yesterday";

  const options = { year: "numeric", month: "short", day: "numeric" };
  return messageDate.toLocaleDateString(undefined, options);
};

export default function CommunityChat() {
  const { chatType } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [inputText, setInputText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [chatData, setChatData] = useState(null);
  const [resolvedChatId, setResolvedChatId] = useState(null);

  // Refs for values the scroll handler needs without re-binding
  const hasMoreRef = useRef(true);
  const loadingOlderRef = useRef(false);
  const oldestCreatedAtRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const pendingScrollRestoreRef = useRef(null);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const pollRef = useRef(null);

  // Profile modal state
  const [selectedUserProfile, setSelectedUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(false);

  const colors = CHAT_COLORS[chatType?.toLowerCase()] || CHAT_COLORS.hiv;
  const chatTitle = `${chatType?.toUpperCase()} Community Chat`;

  const isViewerPremium = Boolean(
    user?.premium_tier &&
    user?.premium_expires_at &&
    new Date(user.premium_expires_at) > new Date()
  );

  // ---------------------------------------------------------------
  // 1) Resolve the chat id (single /public-chats fetch)
  // ---------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setMessages([]);
    setHasMore(true);
    hasMoreRef.current = true;
    oldestCreatedAtRef.current = null;
    setResolvedChatId(null);

    (async () => {
      try {
        const res = await api.get("/public-chats");
        if (cancelled) return;
        const chat = (res.data || []).find(
          (c) => c.chat_type?.toLowerCase() === chatType?.toLowerCase()
        );
        if (chat) {
          setChatData(chat);
          setResolvedChatId(chat.chat_id);
        } else {
          console.warn(`CommunityChat: no chat for type "${chatType}"`);
          setLoading(false);
        }
      } catch (err) {
        console.error("CommunityChat: failed to resolve chat:", err);
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [chatType]);

  // ---------------------------------------------------------------
  // 2) Initial load + 5s poll once we know the chat id
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!resolvedChatId) return;
    let cancelled = false;

    const loadInitial = async () => {
      try {
        const res = await api.get(`/public-chats/${resolvedChatId}/messages`, {
          params: { limit: PAGE_SIZE },
        });
        if (cancelled) return;
        const data = res.data || [];

        setMessages(data);
        const more = data.length >= PAGE_SIZE;
        setHasMore(more);
        hasMoreRef.current = more;

        if (data.length > 0) {
          const oldest = data[0].created_at;
          oldestCreatedAtRef.current = oldest;
        }
        setLoading(false);

        // Scroll to bottom on first paint
        requestAnimationFrame(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
        });
      } catch (err) {
        console.error("CommunityChat: failed to load messages:", err);
        if (!cancelled) setLoading(false);
      }
    };

    // Fetch latest page and append only messages we don't already have
    const fetchNewMessages = async () => {
      try {
        const res = await api.get(`/public-chats/${resolvedChatId}/messages`, {
          params: { limit: PAGE_SIZE },
        });
        if (cancelled) return;
        const data = res.data || [];

        setMessages((prev) => {
          const existing = new Set(prev.map((m) => m.message_id));
          const fresh = data.filter((m) => !existing.has(m.message_id));
          if (fresh.length === 0) return prev;

          if (isNearBottomRef.current) {
            requestAnimationFrame(() => {
              messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
            });
          }
          return [...prev, ...fresh];
        });
      } catch {
        // silent — transient errors shouldn't spam
      }
    };

    loadInitial();

    const startPolling = () => {
      if (pollRef.current) return;
      pollRef.current = setInterval(fetchNewMessages, POLL_INTERVAL_MS);
    };
    const stopPolling = () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };

    startPolling();

    const onVisibility = () => {
      if (document.hidden) stopPolling();
      else {
        fetchNewMessages();
        startPolling();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      stopPolling();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [resolvedChatId]);

  // ---------------------------------------------------------------
  // 3) Load older messages (scroll up) — mirrors Flutter's
  //    _loadOlderMessages: uses `before={oldestCreatedAt}` and
  //    prepends while deduping.
  // ---------------------------------------------------------------
  const loadOlderMessages = useCallback(async () => {
    const chatId = resolvedChatId;
    const oldest = oldestCreatedAtRef.current;
    if (!chatId || loadingOlderRef.current || !hasMoreRef.current || !oldest) return;

    loadingOlderRef.current = true;
    setLoadingOlder(true);

    // Snapshot scroll metrics so we can restore position after prepend
    const container = messagesContainerRef.current;
    const prevHeight = container?.scrollHeight || 0;
    const prevTop = container?.scrollTop || 0;

    try {
      const res = await api.get(`/public-chats/${chatId}/messages`, {
        params: { limit: PAGE_SIZE, before: oldest },
      });
      const data = res.data || [];

      const more = data.length >= PAGE_SIZE;
      hasMoreRef.current = more;
      setHasMore(more);

      if (data.length > 0) {
        const newOldest = data[0].created_at;
        oldestCreatedAtRef.current = newOldest;
      }

      pendingScrollRestoreRef.current = { prevHeight, prevTop };

      setMessages((prev) => {
        const existing = new Set(prev.map((m) => m.message_id));
        const fresh = data.filter((m) => !existing.has(m.message_id));
        if (fresh.length === 0) {
          pendingScrollRestoreRef.current = null;
          return prev;
        }
        // Backend returns ascending, so `fresh` is oldest → newest;
        // prepending keeps chronological order.
        return [...fresh, ...prev];
      });
    } catch (err) {
      console.error("CommunityChat: failed to load older messages:", err);
    } finally {
      loadingOlderRef.current = false;
      setLoadingOlder(false);
    }
  }, [resolvedChatId]);

  // Restore scroll position after older messages have been prepended.
  // Runs after DOM commit, before paint, so no visible jump.
  useLayoutEffect(() => {
    const pending = pendingScrollRestoreRef.current;
    if (!pending) return;
    pendingScrollRestoreRef.current = null;
    const c = messagesContainerRef.current;
    if (c) {
      c.scrollTop = c.scrollHeight - pending.prevHeight + pending.prevTop;
    }
  }, [messages]);

  // ---------------------------------------------------------------
  // 4) Scroll listener — sets "near bottom" flag + triggers older fetch
  // ---------------------------------------------------------------
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      isNearBottomRef.current =
        scrollHeight - scrollTop - clientHeight < NEAR_BOTTOM_THRESHOLD;

      if (
        scrollTop < SCROLL_TOP_THRESHOLD &&
        hasMoreRef.current &&
        !loadingOlderRef.current
      ) {
        loadOlderMessages();
      }
    };

    container.addEventListener("scroll", onScroll, { passive: true });
    return () => container.removeEventListener("scroll", onScroll);
  }, [loadOlderMessages]);

  // Auto-scroll on new incoming messages when user is already near bottom
  useEffect(() => {
    if (isNearBottomRef.current && !pendingScrollRestoreRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // ---------------------------------------------------------------
  // Send
  // ---------------------------------------------------------------
  const handleSend = async () => {
    const text = inputText.trim();
    if (!text || isSending) return;
    if (!resolvedChatId) {
      toast.error("Chat not ready yet, try again in a moment");
      return;
    }

    setIsSending(true);
    setInputText("");

    const replyTo = replyingTo;
    setReplyingTo(null);

    try {
      const payload = { content: text, chat_type: chatData?.chat_type || chatType };
      if (replyTo) payload.reply_to_id = replyTo.message_id;

      const res = await api.post(`/public-chats/${resolvedChatId}/messages`, payload);

      if (res.data?.message) {
        const newMsg = res.data.message;
        if (replyTo) newMsg.reply_to = replyTo;
        setMessages((prev) => {
          if (prev.some((m) => m.message_id === newMsg.message_id)) return prev;
          return [...prev, newMsg];
        });
        isNearBottomRef.current = true;
        requestAnimationFrame(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        });
      }

      await refresh();
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error("Not enough tokens. Earn tokens or upgrade to premium.");
      } else {
        toast.error("Failed to send message");
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.delete(`/public-chats/messages/${messageId}`);
      setMessages((prev) => prev.filter((m) => m.message_id !== messageId));
      toast.success("Message deleted");
    } catch (err) {
      toast.error("Failed to delete message");
    }
  };

  const handleViewProfile = async (userId) => {
    if (!userId || userId === user?.user_id) return;
    setProfileLoading(true);
    try {
      const res = await api.get(`/users/${userId}/profile`);
      setSelectedUserProfile(res.data);
    } catch (err) {
      toast.error("Could not load profile");
    } finally {
      setProfileLoading(false);
    }
  };

  const handleSendRequest = async (type) => {
    if (!selectedUserProfile || sendingRequest) return;
    setSendingRequest(true);
    try {
      const swipeType = type === "dating" ? "dating" : "friendship";
      const result = await swipeProfile(selectedUserProfile.user_id, "like", swipeType);

      if (result.matched) {
        toast.success("💜 It's a match!", {
          description: `You matched with ${selectedUserProfile.display_name || "someone"}!`,
        });
      } else {
        toast.success(type === "dating" ? "Dating request sent!" : "Friend request sent!");
      }
      setSelectedUserProfile(null);
    } catch (err) {
      toast.error("Failed to send request");
    } finally {
      setSendingRequest(false);
    }
  };

  const startReply = (msg) => setReplyingTo(msg);
  const cancelReply = () => setReplyingTo(null);

  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const hours = date.getHours().toString().padStart(2, "0");
    const mins = date.getMinutes().toString().padStart(2, "0");
    return `${hours}:${mins}`;
  };

  const groupMessagesByDate = (msgs) => {
    const groups = {};
    msgs.forEach((msg) => {
      const date = new Date(msg.created_at);
      const key = formatDateHeader(date);
      if (!groups[key]) groups[key] = [];
      groups[key].push(msg);
    });
    return Object.entries(groups);
  };

  const getAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birth = new Date(dateOfBirth);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  return (
    <div className="min-h-screen bg-[#EFEAE2]">
      <Navbar />
      <main className="max-w-2xl mx-auto h-[calc(100vh-4rem)] flex flex-col">
        {/* Header */}
        <div className="bg-[#F0F2F5] border-b px-4 py-3 flex items-center gap-3 flex-shrink-0">
          <button onClick={() => navigate("/stories")} className="p-1 hover:bg-gray-200 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className={`w-10 h-10 rounded-full ${colors.lightBg} flex items-center justify-center text-lg`}>
            💬
          </div>
          <div>
            <h3 className="font-semibold text-sm text-gray-900">{chatTitle}</h3>
            <p className="text-xs text-gray-500">{messages.length} messages</p>
          </div>
        </div>

        {/* Messages */}
        <div
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto px-3 py-3 space-y-1"
          style={{
            backgroundImage:
              "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAYAAAAeP4ixAAAABHNCSVQICAgIfAhkiAAAAAlwSFlzAAAOxAAADsQBlSsOGwAAABl0RVh0U29mdHdhcmUAd3d3Lmlua3NjYXBlLm9yZ5vuPBoAAAAWdEVYdFRpdGxlAEJlYWNvbi5qcGVnQ1JFV0EAAAKsSURBVGiB7ZpNjtQwEEa/4gTsOUJvOEJL4gJIiAuw5QhICFbsWMI1eo4GCdHdLjtJ2amfqspf8j5p1Op2+XPZVU4nKVlZeZKkOyLiKiI+IuI2Ij4j4nO59j9K1j7LelzW5+8REWtE/FnLD7oHWDtrr8AJcDa5Tw/gj61qEwhsflK9v0TEr2WMfx7gI1X7+q4JTG/eAx4YcJvALIHpvUZEO0dawIAXX8AD4G0CC4DXSZnNA74D3MqvteCcIQ7MUzScC3JDHwHuD+B9JqwBfKMxluMALnrA+0w4MRbGcfQwBnj2BHBfB3xPwtqLfMw81tLyGBCdAW+JcDKhvYX07C5yP0/9FuvB1QSYkwlrI9BYTqjGJmGgKox7gB8RtXACsN6BngMME4BXmq8C/BmTi/wA/FyLtjEDcPx7x74E8GHyHlMP8Gj8jR7gLOLfFWCdk3gNuPzJQVa/wt1rsc8M0O3b1gSQTqMmz9QO03dk8YQlMl+R/jXLBFss9tFrwCev78xzGpv5crtQ6++zTP+O0s1t7oGL/jkP9OmfgF5u1S7ujC+QLx4bICJE4P3bIwFfvGVdGeLDM54C7t/PbwgAPuRMNYUx9xR1QdSsyCk8FgzC4wD3zPAVOORkzty2AM6BdY6BzM84oow0S3mAC8BJcTTjTwfwGfBm3KoCbsazHeBTD7ifP93iHSDdby8O8K4PeIAcnbuqAY4C4wFf2IAXDa5rnhp5nYHh+fOBTX9sg80iS1d1zxK7M1r8H9m96PJd7W2lBD5OudmlJdAc1+oLk/bNyOXrLNo4EONIvgCu83I0OpxMboGDJvXH2sUGNwuTBNzO8Vqz+4K/AYynffDA3GlvAAAAAElFTkSuQmCC')",
            backgroundColor: "#EFEAE2",
          }}
        >
          {/* Loading older indicator at the top */}
          {loadingOlder && (
            <div className="flex justify-center py-2">
              <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {!loadingOlder && hasMore && messages.length > 0 && (
            <div className="flex justify-center py-1">
              <span className="text-[10px] text-gray-400">Scroll up for older messages</span>
            </div>
          )}
          {!hasMore && messages.length > 0 && (
            <div className="flex justify-center py-1">
              <span className="text-[10px] text-gray-400">— Beginning of chat —</span>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex items-center justify-center h-full text-center">
              <div>
                <div className="text-4xl mb-3">💬</div>
                <p className="text-sm text-gray-500">No messages yet</p>
                <p className="text-xs text-gray-400 mt-1">Say hello to the community! 👋</p>
              </div>
            </div>
          ) : (
            groupMessagesByDate(messages).map(([dateLabel, msgs]) => (
              <div key={dateLabel}>
                <div className="flex justify-center my-3">
                  <span className="px-3 py-1 bg-white/80 backdrop-blur-sm text-xs text-gray-500 rounded-full shadow-sm">
                    {dateLabel}
                  </span>
                </div>
                {msgs.map((msg, idx) => {
                  const isMe = msg.sender_id === user?.user_id;
                  const showAvatar = !isMe && (idx === 0 || msgs[idx - 1]?.sender_id !== msg.sender_id);
                  const avatarUrl = msg.sender_avatar || null;

                  return (
                    <div
                      key={msg.message_id}
                      className={`flex ${isMe ? "justify-end" : "justify-start"} items-end gap-1`}
                    >
                      {!isMe && (
                        <div
                          onClick={() => handleViewProfile(msg.sender_id)}
                          className="w-9 h-9 rounded-full overflow-hidden bg-gray-300 flex-shrink-0 mb-0.5 cursor-pointer hover:ring-2 ring-purple-300 transition-all"
                          title={`View ${msg.sender_name || "user"}'s profile`}
                        >
                          {showAvatar ? (
                            avatarUrl ? (
                              <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-[11px] font-bold text-white">
                                {(msg.sender_name || "?")[0].toUpperCase()}
                              </div>
                            )
                          ) : (
                            <div className="w-9 h-9" />
                          )}
                        </div>
                      )}

                      <div className="max-w-[75%] group">
                        {msg.reply_to && (
                          <div
                            className={`mb-0.5 px-2 py-1 rounded-t-lg text-[11px] ${
                              isMe ? "bg-[#025144]/30 text-white/90" : "bg-gray-200/80 text-gray-600"
                            }`}
                          >
                            <span className="font-medium">{msg.reply_to.sender_name}</span>
                            <p className="truncate opacity-80">{msg.reply_to.content}</p>
                          </div>
                        )}

                        <div className="relative">
                          {!isMe && (
                            <div
                              className="absolute top-0 -left-[6px] w-2 h-2 bg-white"
                              style={{ clipPath: "polygon(100% 0, 0 0, 100% 100%)" }}
                            />
                          )}
                          {isMe && (
                            <div
                              className="absolute bottom-0 -right-[6px] w-2 h-2 bg-[#005C4B]"
                              style={{ clipPath: "polygon(0 0, 100% 0, 0 100%)" }}
                            />
                          )}

                          <div
                            className={`relative ${
                              isMe
                                ? "bg-[#005C4B] text-white rounded-lg rounded-br-sm"
                                : "bg-white text-gray-900 rounded-lg rounded-tl-sm"
                            } shadow-sm`}
                          >
                            {!isMe && (
                              <button
                                onClick={() => handleViewProfile(msg.sender_id)}
                                className="px-2.5 pt-1.5 pb-0 text-[11px] font-semibold text-[#005C4B] cursor-pointer hover:underline"
                              >
                                {msg.sender_name}
                              </button>
                            )}
                            <div className={`${!isMe ? "px-2.5 pb-1.5 pt-0" : "px-2.5 py-1.5"} pr-16`}>
                              <p className="text-[14px] leading-[1.4] whitespace-pre-wrap break-words">
                                {msg.content}
                              </p>
                            </div>

                            <div
                              className={`absolute bottom-0.5 right-2 flex items-center gap-1 text-[10px] ${
                                isMe ? "text-white/55" : "text-gray-400"
                              }`}
                            >
                              <button
                                onClick={() => startReply(msg)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity hover:text-white mr-0.5"
                                title="Reply"
                              >
                                ↩
                              </button>
                              {isMe && (
                                <button
                                  onClick={() => handleDelete(msg.message_id)}
                                  className="opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-300 mr-0.5"
                                  title="Delete"
                                >
                                  🗑
                                </button>
                              )}
                              <span>{formatTime(msg.created_at)}</span>
                              {isMe && <Check className="w-3 h-3" />}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {replyingTo && (
          <div className="px-4 py-2 bg-[#F0F2F5] border-t flex items-center gap-3 flex-shrink-0">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-[#005C4B]">
                Replying to {replyingTo.sender_name}
              </p>
              <p className="text-xs text-gray-500 truncate">{replyingTo.content}</p>
            </div>
            <button onClick={cancelReply} className="p-1 hover:bg-gray-200 rounded">
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        )}

        <div className="bg-[#F0F2F5] px-3 py-2 flex-shrink-0">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
              placeholder="Type a message..."
              className="flex-1 px-4 py-2.5 bg-white rounded-full text-sm outline-none border-none"
              disabled={isSending}
            />
            <button
              onClick={handleSend}
              disabled={!inputText.trim() || isSending || !resolvedChatId}
              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                !inputText.trim() || isSending || !resolvedChatId
                  ? "bg-gray-300 text-gray-400"
                  : "bg-[#005C4B] text-white"
              }`}
            >
              {isSending ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </main>

      {/* Profile Modal */}
      {selectedUserProfile && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => setSelectedUserProfile(null)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-[#E7E5E0]">
              <h3 className="font-display text-lg font-semibold flex items-center gap-2">
                <Info className="w-5 h-5" /> Profile
              </h3>
              <button
                onClick={() => setSelectedUserProfile(null)}
                className="p-1 hover:bg-[#F5F3EE] rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-full overflow-hidden bg-[#F5F3EE] flex-shrink-0">
                  {selectedUserProfile.profile_image ? (
                    <img
                      src={selectedUserProfile.profile_image}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-3xl">👤</div>
                  )}
                </div>
                <div>
                  <h4 className="font-semibold text-lg">
                    {selectedUserProfile.display_name || "Anonymous"}
                    {selectedUserProfile.verified && (
                      <span className="ml-1 text-blue-500">✓</span>
                    )}
                  </h4>
                  <p className="text-sm text-gray-500">
                    {getAge(selectedUserProfile.date_of_birth) &&
                      `${getAge(selectedUserProfile.date_of_birth)} · `}
                    {selectedUserProfile.gender}
                  </p>
                </div>
              </div>

              {selectedUserProfile.country && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  {selectedUserProfile.city ? `${selectedUserProfile.city}, ` : ""}
                  {selectedUserProfile.country}
                </div>
              )}

              {selectedUserProfile.bio && (
                <p className="text-sm text-gray-700 leading-relaxed">{selectedUserProfile.bio}</p>
              )}

              {selectedUserProfile.interests && (
                <div className="flex flex-wrap gap-1.5">
                  {selectedUserProfile.interests.split(",").map((i, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 bg-purple-50 text-purple-700 rounded-full text-xs"
                    >
                      {i.trim()}
                    </span>
                  ))}
                </div>
              )}

              <div className="flex gap-3 pt-4 border-t border-[#E7E5E0]">
                <button
                  onClick={() => handleSendRequest("dating")}
                  disabled={sendingRequest}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 py-3 rounded-xl text-sm font-medium bg-rose-500 text-white hover:bg-rose-600 disabled:opacity-50 transition-colors"
                >
                  <Heart className="w-4 h-4" />
                  {sendingRequest ? <Loader2 className="w-4 h-4 animate-spin" /> : "Date"}
                </button>
                <button
                  onClick={() => handleSendRequest("friend")}
                  disabled={sendingRequest}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 py-3 rounded-xl text-sm font-medium bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 transition-colors"
                >
                  <Users className="w-4 h-4" />
                  {sendingRequest ? <Loader2 className="w-4 h-4 animate-spin" /> : "Friend"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {profileLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <Loader2 className="w-8 h-8 animate-spin text-white" />
        </div>
      )}
    </div>
  );
}